// =============================================================================
// AR / Invoices dashboard — React port of the ar-due prototype (Aug 05 spec).
//
// Two views behind one toggle:
//   📋 Invoices register — all invoices newest first (GET /invoice-dashboard/invoices)
//   📅 Due buckets       — collections console over GET /invoice-dashboard/due-soon
//      (4 aging tabs + the 🔔 Reminders dry-run tab), with the payment lens
//      (Owed / Paid-ahead / All), group-by drill-down (Invoice / Hospital /
//      Sales rep / Territory), plan + rep filters and client-side CSV export.
//
// Spec rules honored here:
//   • loading states are NOT optional — due-soon reads live Epicor balances
//     (~1–20s); a slow bucket must never show the previous bucket's rows.
//   • liveEnriched=false ⇒ banner: amounts are gross, paid items may appear;
//     the Paid lens shows an explicit empty state instead of guessing.
//   • only problem match verdicts surface on list rows; clean stays quiet.
// Row click → /invoices/{invoiceNum} (the one-call detail screen).
// =============================================================================
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Alert, Badge, Button, Form, Spinner } from "react-bootstrap";
import { FaDownload, FaFileInvoiceDollar, FaSync } from "react-icons/fa";
import { apiFetch, getUser, isAdminOrDistrRole } from "../utils/auth";
import {
  BUCKETS, GROUPBYS, MATCH_BADGE, PAYFILTERS, PLANS, PROBLEM_MATCHES,
  daysLabel, exportCsv, fmtDate, groupByParent, isPaidItem, money,
  seqChip, terrOf, worstMatch,
} from "./arShared";
import "../pagecss/Webappcss.css";
import "../pagecss/QuoteWizard.css";

const API_URL = process.env.REACT_APP_API_URL;

const chip = (active) => ({
  padding: "5px 12px", borderRadius: 999, fontSize: 12.5, fontWeight: 600, cursor: "pointer",
  border: active ? "1.5px solid #4F46E5" : "1px solid #E2E8F0",
  background: active ? "#EEF2FF" : "#fff", color: active ? "#4338CA" : "#475569",
});

// Uniform compact select for the control panel (matches the 31px button height).
const SEL = { padding: "5px 10px", height: 31, width: 150, borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12.5, background: "#fff", color: "#334155" };

// Full-width segmented view tabs (Invoices / Due buckets) — a slate track with
// a raised gradient pill for the active tab; each tab takes an equal share.
const segTab = (active) => ({
  flex: 1, textAlign: "center", padding: "9px 14px", cursor: "pointer", borderRadius: 9,
  fontSize: 13, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
  color: active ? "#fff" : "#475569",
  background: active ? "linear-gradient(135deg, #4F46E5, #6366F1)" : "transparent",
  boxShadow: active ? "0 2px 8px rgba(79,70,229,.35)" : "none",
  transition: "all .15s ease",
});

// Standard grid header/cell styles — same look as the Quote/Shipment tables.
const TH = { padding: "7px 12px", fontWeight: 700, whiteSpace: "nowrap" };
const THF = { padding: "6px 8px", borderTop: "1px solid #E2E8F0", verticalAlign: "top", background: "#F8FAFC" };
const HEAD_TR = { background: "#F8FAFC", textAlign: "left", color: "#64748B", fontSize: 11, textTransform: "uppercase", letterSpacing: ".04em" };
const SUB = { fontSize: 11, color: "#94A3B8" };

// Column-filter substring match: empty filter passes; any candidate value can hit.
const hit = (f, ...vals) => {
  const q = (f || "").trim().toLowerCase();
  return !q || vals.some((v) => String(v ?? "").toLowerCase().includes(q));
};

function Kpi({ label, value, tone }) {
  return (
    <div style={{ minWidth: 120, padding: "10px 14px", border: "1px solid #E2E8F0", borderRadius: 10, background: "#fff" }}>
      <div style={{ fontSize: 11, color: "#94A3B8", textTransform: "uppercase", letterSpacing: ".05em" }}>{label}</div>
      <div style={{ fontSize: 17, fontWeight: 700, color: tone || "#0F172A" }}>{value}</div>
    </div>
  );
}

export default function ARDashboard() {
  const navigate = useNavigate();

  const [view, setView] = useState("register");        // register | buckets
  const [bucket, setBucket] = useState("today");       // BUCKETS key | "reminders"
  const [planF, setPlanF] = useState("ALL");
  const [payF, setPayF] = useState("owed");
  const [groupBy, setGroupBy] = useState("invoice");
  const [repF, setRepF] = useState("");

  const [data, setData] = useState(null);              // due-soon response OR register response
  const [reminders, setReminders] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [counts, setCounts] = useState({});            // bucketKey → {count, remaining}
  const [repNames, setRepNames] = useState({});        // code → name
  const [openGroups, setOpenGroups] = useState({});
  const [colF, setColF] = useState({});                // per-column grid filters
  const loadSeq = useRef(0);                           // stale-response guard
  const setCol = (k, v) => setColF((o) => ({ ...o, [k]: v }));

  // ── Manual invoice generation (admin-only; mirrors the API's admin-equivalent gate) ──
  const isAdmin = useMemo(() => isAdminOrDistrRole(getUser().role), []);
  const [genDryRun, setGenDryRun] = useState(true);    // preview-first by default
  const [genBusy, setGenBusy] = useState(null);        // which action is running ("N30" | … | "RECONCILE")
  const [genResult, setGenResult] = useState(null);    // { label, ok, dry, msg, preview[], failures[] }

  // ── loads ──────────────────────────────────────────────────────────────────
  const loadRegister = useCallback(async () => {
    const seq = ++loadSeq.current;
    setLoading(true); setError(null); setData(null);
    try {
      const cat = PLANS[planF]?.cat;
      const r = await apiFetch(`${API_URL}/api/invoice-dashboard/invoices?sort=invoice&pageSize=500${cat ? `&planCategory=${cat}` : ""}`);
      const d = await r.json().catch(() => null);
      if (loadSeq.current !== seq) return;
      if (!r.ok || d?.success === false) throw new Error(d?.error || `Load failed (HTTP ${r.status}).`);
      setData({ kind: "register", ...d });
    } catch (e) { if (loadSeq.current === seq) setError(e.message); }
    finally { if (loadSeq.current === seq) setLoading(false); }
  }, [planF]);

  const loadBucket = useCallback(async (key, lens) => {
    const seq = ++loadSeq.current;
    setLoading(true); setError(null); setData(null);
    try {
      const b = BUCKETS[key];
      const cat = PLANS[planF]?.cat;
      const qs = `fromDays=${b.fromDays}&toDays=${b.toDays}&includeSent=true`
        + (lens !== "owed" ? "&includePaid=true" : "")
        + (cat ? `&planCategory=${cat}` : "");
      const r = await apiFetch(`${API_URL}/api/invoice-dashboard/due-soon?${qs}`);
      const d = await r.json().catch(() => null);
      if (loadSeq.current !== seq) return;
      if (!r.ok || d?.success === false) throw new Error(d?.error || `Load failed (HTTP ${r.status}).`);
      setData({ kind: "bucket", bucket: key, ...d });
    } catch (e) { if (loadSeq.current === seq) setError(e.message); }
    finally { if (loadSeq.current === seq) setLoading(false); }
  }, [planF]);

  const loadReminders = useCallback(async () => {
    const seq = ++loadSeq.current;
    setLoading(true); setError(null); setReminders(null);
    try {
      const r = await apiFetch(`${API_URL}/api/reminders/preview`);
      const d = await r.json().catch(() => null);
      if (loadSeq.current !== seq) return;
      if (!r.ok) throw new Error(d?.error || `Load failed (HTTP ${r.status}).`);
      setReminders(d);
    } catch (e) { if (loadSeq.current === seq) setError(e.message); }
    finally { if (loadSeq.current === seq) setLoading(false); }
  }, []);

  // CTO reconcile-and-bill — releases due/validated/matched later months of approved CTO invoices.
  // (The per-plan N30/BK/PoT/CTO generate buttons were removed from this row on
  // request; the dry-run toggle and Reconcile CTO remain.)
  const runReconcileCto = useCallback(async () => {
    if (!window.confirm("Run CTO reconcile-and-bill now?\n\nReleases the due, validated, strict-matched later months (2..N) of approved consign-to-own invoices as their POs arrive.")) return;
    setGenBusy("RECONCILE"); setGenResult(null);
    try {
      const r = await apiFetch(`${API_URL}/api/invoices/reconcile-cto`, { method: "POST" });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || d?.success === false) throw new Error(d?.error || `HTTP ${r.status}`);
      const released = d.released ?? d.releasedCount ?? d.billedCount;
      setGenResult({ label: "CTO reconcile", ok: true, dry: false, msg: `CTO reconcile done${typeof released === "number" ? ` — ${released} month(s) released` : ""}`, preview: [], failures: d.failures || [] });
      setCounts({}); loadRegister();
    } catch (e) {
      setGenResult({ label: "CTO reconcile", ok: false, msg: `Reconcile failed: ${e.message}` });
    } finally { setGenBusy(null); }
  }, [loadRegister]);

  // Background: tab badges (one due-soon per bucket) — the "5× surprise".
  const refreshCounts = useCallback(() => {
    const cat = PLANS[planF]?.cat;
    Object.entries(BUCKETS).forEach(([key, b]) => {
      apiFetch(`${API_URL}/api/invoice-dashboard/due-soon?fromDays=${b.fromDays}&toDays=${b.toDays}&includeSent=true${cat ? `&planCategory=${cat}` : ""}`)
        .then((r) => r.json()).then((d) => {
          if (d?.success !== false)
            setCounts((c) => ({ ...c, [key]: { count: d.count, remaining: d.totalRemaining } }));
        }).catch(() => {});
    });
  }, [planF]);

  // Rep code → name map (best-effort; codes render if the lookup fails).
  useEffect(() => {
    apiFetch(`${API_URL}/api/SalesRep?Top=5000`)
      .then((r) => r.json())
      .then((d) => {
        const list = d?.salesReps || d?.items || d?.value || (Array.isArray(d) ? d : []);
        const map = {};
        for (const s of list) {
          const code = s.salesRepCode || s.SalesRepCode;
          const name = s.name || s.Name;
          if (code) map[code] = name || code;
        }
        setRepNames(map);
      }).catch(() => {});
  }, []);

  useEffect(() => {
    setOpenGroups({});
    setColF({});
    if (view === "register") loadRegister();
    else if (bucket === "reminders") loadReminders();
    else { loadBucket(bucket, payF); refreshCounts(); }
  }, [view, bucket, payF, loadRegister, loadBucket, loadReminders, refreshCounts]);

  // ── derived rows ───────────────────────────────────────────────────────────
  const notLive = data?.kind === "bucket" && data.liveEnriched === false;

  const bucketRows = useMemo(() => {
    if (data?.kind !== "bucket") return [];
    let items = data.items || [];
    if (repF) items = items.filter((i) => i.salesRepCode === repF);
    if (payF === "owed") items = items.filter((i) => !isPaidItem(i));
    else if (payF === "paid") items = items.filter((i) => isPaidItem(i));
    return groupByParent(items);
  }, [data, repF, payF]);

  const registerRows = useMemo(() => {
    if (data?.kind !== "register") return [];
    let items = data.items || [];
    if (repF) items = items.filter((i) => i.salesRepCode === repF);
    return items;
  }, [data, repF]);

  const repOptions = useMemo(() => {
    const src = data?.kind === "register" ? (data.items || []) : (data?.items || []);
    const codes = [...new Set(src.map((i) => i.salesRepCode).filter(Boolean))].sort();
    return codes;
  }, [data]);

  // Per-column grid filters (client-side, like the Quote/Shipment tables).
  const visibleRegister = useMemo(() => registerRows.filter((i) =>
    hit(colF.invoice, i.invoiceNum, i.installmentCount > 1 ? `${i.installmentCount} installments` : "")
    && hit(colF.customer, i.customerName, i.custNum)
    && hit(colF.plan, i.planCategory)
    && hit(colF.rep, i.salesRepCode, repNames[i.salesRepCode])
    && hit(colF.amount, i.invoiceAmount, money(i.invoiceAmount))
    && hit(colF.date, fmtDate(i.invoiceDate))
    && hit(colF.match, i.matchStatus)
    && hit(colF.sent, i.sendStatus)
    && hit(colF.po, i.hasPO ? "PO yes" : "no PO")
    && hit(colF.held, i.isHeld ? "held yes" : "no")
  ), [registerRows, colF, repNames]);

  const visibleBucket = useMemo(() => bucketRows.filter((g) =>
    hit(colF.invoice, g.parentInvoiceNum, g.isExternal ? "external" : "")
    && hit(colF.customer, g.customerName, g.custNum)
    && hit(colF.plan, g.planCategory)
    && hit(colF.rep, g.salesRepCode, repNames[g.salesRepCode])
    && hit(colF.inst, seqChip(g))
    && hit(colF.sent, `${g.sent}/${g.seqs.length}`, g.sent === g.seqs.length ? "sent" : "")
    && hit(colF.po, `${g.po}/${g.seqs.length}`, g.po === g.seqs.length ? "PO yes" : "")
    && hit(colF.match, worstMatch(g.matches))
    && hit(colF.amount, g.rem, money(g.rem), money(g.gross))
    && hit(colF.paid, g.paidIn > 0.004 ? money(g.paidIn) : "")
    && hit(colF.due, [...g.dueDates].map((d) => fmtDate(d)).join(" "))
    && hit(colF.days, daysLabel(g.minDays))
  ), [bucketRows, colF, repNames]);

  // ── export ─────────────────────────────────────────────────────────────────
  const doExport = () => {
    if (data?.kind === "bucket") {
      exportCsv(
        `AR_${data.bucket}${planF !== "ALL" ? `_${planF}` : ""}${payF !== "owed" ? `_${payF}` : ""}.csv`,
        ["Invoice", "Customer", "Plan", "Amount due", "Paid so far", "Due date(s)", "Days overdue", "Installments", "Sent", "PO captured"],
        visibleBucket.map((g) => [
          g.parentInvoiceNum, g.customerName, PLANS[Object.keys(PLANS).find((k) => PLANS[k].cat === g.planCategory)]?.label || g.planCategory,
          g.rem.toFixed(2), g.paidIn.toFixed(2), [...g.dueDates].join(" "),
          g.minDays < 0 ? -g.minDays : 0, seqChip(g), `${g.sent}/${g.seqs.length}`, `${g.po}/${g.seqs.length}`,
        ])
      );
    } else if (data?.kind === "register") {
      exportCsv(
        `AR_register${planF !== "ALL" ? `_${planF}` : ""}.csv`,
        ["Invoice", "Customer", "Plan", "Amount", "Invoice date", "Match", "Sent", "Has PO", "Held"],
        visibleRegister.map((i) => [
          i.invoiceNum, i.customerName, i.planCategory, i.invoiceAmount, fmtDate(i.invoiceDate),
          i.matchStatus, i.sendStatus, i.hasPO ? "Y" : "N", i.isHeld ? "Y" : "N",
        ])
      );
    }
  };

  // ── row renderers ──────────────────────────────────────────────────────────
  const openInvoice = (num) => navigate(`/invoices/${num}`);

  const urgency = (minDays) =>
    minDays < 0 ? "#DC2626" : minDays === 0 ? "#B45309" : minDays <= 7 ? "#2563EB" : "#64748B";

  function BucketTr({ g }) {
    const paidLens = payF === "paid";
    const worst = worstMatch(g.matches);
    const showWorst = worst && PROBLEM_MATCHES.includes(worst);
    const tone = paidLens ? "#16A34A" : urgency(g.minDays);
    return (
      <tr style={{ cursor: "pointer" }} onClick={() => openInvoice(g.parentInvoiceNum)}>
        <td data-label="Invoice #" style={{ borderLeft: `4px solid ${tone}` }}>
          <span style={{ fontWeight: 700, color: "#4338CA" }}>{g.parentInvoiceNum}</span>
          {g.isExternal && <div><Badge bg="info" title="Created in Epicor, adopted for collections — not auto-sent">External</Badge></div>}
        </td>
        <td data-label="Customer">
          <div style={{ fontWeight: 600 }}>{g.customerName || `Cust ${g.custNum}`}</div>
          <div style={SUB}>cust {g.custNum}</div>
        </td>
        <td data-label="Plan"><Badge bg="light" text="dark" style={{ border: "1px solid #E2E8F0" }}>{g.planCategory}</Badge></td>
        <td data-label="Sales Rep">{g.salesRepCode ? (repNames[g.salesRepCode] || g.salesRepCode) : "—"}</td>
        <td data-label="Installments">{seqChip(g)}</td>
        <td data-label="Sent">
          <Badge bg={g.sent === g.seqs.length ? "success" : g.sent > 0 ? "warning" : "secondary"} text={g.sent > 0 && g.sent < g.seqs.length ? "dark" : undefined}>
            {g.sent === g.seqs.length ? "Sent" : g.sent > 0 ? `${g.sent}/${g.seqs.length} sent` : "Not sent"}
          </Badge>
        </td>
        <td data-label="PO"><Badge bg={g.po === g.seqs.length ? "success" : "secondary"}>{g.po === g.seqs.length ? "PO ✔" : `PO ${g.po}/${g.seqs.length}`}</Badge></td>
        <td data-label="Match">{showWorst ? <Badge bg={MATCH_BADGE[worst] || "secondary"}>{worst}</Badge> : "—"}</td>
        <td data-label="Amount Due" style={{ textAlign: "right" }}>
          <div style={{ fontWeight: 700, color: paidLens ? "#16A34A" : "#0F172A" }}>
            {paidLens ? money(g.paidIn || g.gross - g.rem) : money(g.rem)}
          </div>
          {g.rem < g.gross - 0.004 && !paidLens && (
            <div style={{ ...SUB, textDecoration: "line-through" }}>{money(g.gross)}</div>
          )}
        </td>
        <td data-label="Paid So Far" style={{ textAlign: "right", color: g.paidIn > 0.004 ? "#16A34A" : "#94A3B8" }}
          title={g.paidIn > 0.004 && !paidLens ? "already paid — open for receipts" : undefined}>
          {g.paidIn > 0.004 ? `💳 ${money(g.paidIn)}` : "—"}
        </td>
        <td data-label="Due Date(s)">{[...g.dueDates].map((d) => fmtDate(d)).join(" · ") || "—"}</td>
        <td data-label="Due" style={{ color: tone, fontWeight: 600, whiteSpace: "nowrap" }}>
          {paidLens
            ? (g.minDays > 0 ? `due ${daysLabel(g.minDays)} — covered` : g.minDays === 0 ? "due today — paid" : `${-g.minDays}d ago — cleared`)
            : daysLabel(g.minDays)}
        </td>
      </tr>
    );
  }

  function RegisterTr({ i }) {
    return (
      <tr style={{ cursor: "pointer" }} onClick={() => openInvoice(i.invoiceNum)}>
        <td data-label="Invoice #">
          <span style={{ fontWeight: 700, color: "#4338CA" }}>{i.invoiceNum}</span>
          {i.installmentCount > 1 && <div style={SUB}>{i.installmentCount} installments</div>}
        </td>
        <td data-label="Customer">
          <div style={{ fontWeight: 600 }}>{i.customerName || `Cust ${i.custNum}`}</div>
          <div style={SUB}>cust {i.custNum}</div>
        </td>
        <td data-label="Plan"><Badge bg="light" text="dark" style={{ border: "1px solid #E2E8F0" }}>{i.planCategory}</Badge></td>
        <td data-label="Sales Rep">{i.salesRepCode ? (repNames[i.salesRepCode] || i.salesRepCode) : "—"}</td>
        <td data-label="Amount" style={{ textAlign: "right", fontWeight: 700 }}>{money(i.invoiceAmount)}</td>
        <td data-label="Invoice Date">{fmtDate(i.invoiceDate)}</td>
        <td data-label="Match"><Badge bg={MATCH_BADGE[i.matchStatus] || "secondary"}>{i.matchStatus}</Badge></td>
        <td data-label="Sent"><Badge bg={i.sendStatus === "Sent" ? "success" : "secondary"}>{i.sendStatus}</Badge></td>
        <td data-label="PO"><Badge bg={i.hasPO ? "success" : "secondary"}>{i.hasPO ? "PO ✓" : "no PO"}</Badge></td>
        <td data-label="Held">{i.isHeld ? <Badge bg="warning" text="dark">🔒 held</Badge> : "—"}</td>
      </tr>
    );
  }

  // Grouped view (Hospital / Sales rep / Territory) — groups sorted by $ desc.
  const grouped = useMemo(() => {
    if (data?.kind !== "bucket" || groupBy === "invoice") return null;
    const keyOf = (g) =>
      groupBy === "hospital" ? (g.customerName || `Cust ${g.custNum}`)
        : groupBy === "rep" ? (repNames[g.salesRepCode] || g.salesRepCode || "(no rep)")
        : terrOf(g.salesRepCode) || "(no territory)";
    const map = new Map();
    for (const g of visibleBucket) {
      const k = keyOf(g);
      let grp = map.get(k);
      if (!grp) { grp = { key: k, total: 0, ahead: 0, invoices: [], over90: 0, unsent: 0, noPo: 0, minDays: Infinity }; map.set(k, grp); }
      grp.total += payF === "paid" ? (g.paidIn || g.gross - g.rem) : g.rem;
      grp.invoices.push(g);
      if (g.minDays <= -90) grp.over90 += g.rem;
      if (g.sent < g.seqs.length) grp.unsent++;
      if (g.po < g.seqs.length) grp.noPo++;
      grp.minDays = Math.min(grp.minDays, g.minDays);
    }
    return [...map.values()].sort((a, b) => b.total - a.total);
  }, [data, groupBy, visibleBucket, repNames, payF]);

  // ── reminders view ─────────────────────────────────────────────────────────
  function RemindersView() {
    if (!reminders) return null;
    const rows = reminders.reminders || reminders.items || reminders.rows || [];
    return (
      <div>
        <Alert variant="info" style={{ fontSize: 13 }}>
          🎬 <b>Simulate</b> — this is what the daily reminder run WOULD send today; nothing is emailed
          until <code>ReminderEngine:Enabled=true</code>.
        </Alert>
        {rows.length === 0 ? (
          <div style={{ textAlign: "center", padding: 32, color: "#64748B" }}>No reminders would go out today. ✅</div>
        ) : (
          <div style={{ border: "1px solid #E2E8F0", borderRadius: 10, background: "#fff" }}>
            <table className="dyn-table-dense fin-table" style={{ width: "100%", tableLayout: "fixed", borderCollapse: "collapse", fontSize: 13 }}>
              <colgroup>
                <col style={{ width: "10%" }} />
                <col style={{ width: "10%" }} />
                <col style={{ width: "22%" }} />
                <col style={{ width: "10%" }} />
                <col style={{ width: "24%" }} />
                <col style={{ width: "24%" }} />
              </colgroup>
              <thead>
                <tr style={HEAD_TR}>
                  <th style={TH}>Type</th>
                  <th style={TH}>Invoice #</th>
                  <th style={TH}>Customer</th>
                  <th style={{ ...TH, textAlign: "right" }}>Amount</th>
                  <th style={TH}>To</th>
                  <th style={TH}>Why</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i}>
                    <td data-label="Type"><Badge bg={r.customerFacing ?? (r.kind || r.type || "").toLowerCase().includes("customer") ? "primary" : "warning"} text={r.customerFacing ? undefined : "dark"}>{r.kind || r.type || (r.customerFacing ? "Customer" : "Internal")}</Badge></td>
                    <td data-label="Invoice #">{r.invoiceNum || r.parentInvoiceNum || "—"}</td>
                    <td data-label="Customer">{r.customerName || "—"}</td>
                    <td data-label="Amount" style={{ textAlign: "right" }}>{money(r.amount ?? r.remaining ?? r.openRemaining)}</td>
                    <td data-label="To" style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{r.to || r.toEmail || (r.customerFacing ? "(no contact resolved)" : "(internal team)")}</td>
                    <td data-label="Why" style={{ color: "#64748B" }}>{r.why || r.reason || r.detail || ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  }

  // ── render ─────────────────────────────────────────────────────────────────
  const b = BUCKETS[bucket];
  return (
    <div style={{ display: "flow-root" }}>
    <div className="qw-root content-halo dyn-qw dyn-search-shell">
      <div className="qw-header" style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div className="qw-header-icon"><FaFileInvoiceDollar size={18} /></div>
          <div>
            <div className="qw-header-title">AR Invoice Dashboard</div>
            <div className="qw-header-sub">invoices &amp; collections</div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <Button className="qw-btn qw-btn-sm" style={{ background: "rgba(255,255,255,.18)", color: "#fff", border: "none", fontWeight: 600 }}
            onClick={() => { setCounts({}); view === "register" ? loadRegister() : bucket === "reminders" ? loadReminders() : loadBucket(bucket, payF); }}>
            <FaSync />
          </Button>
          <Button className="qw-btn qw-btn-sm" style={{ background: "rgba(255,255,255,.18)", color: "#fff", border: "none", fontWeight: 600 }} onClick={doExport} disabled={loading}>
            <FaDownload style={{ marginRight: 6 }} /> Export to Excel
          </Button>
        </div>
      </div>

      {/* Control panel — one card so every row shares the same width and rhythm:
          view tabs on top, then filters (left) + admin generation actions (right). */}
      <div style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12, padding: 12, marginBottom: 14, display: "flex", flexDirection: "column", gap: 10 }}>
        <div style={{ display: "flex", gap: 4, background: "#F1F5F9", borderRadius: 10, padding: 4 }}>
          <span style={segTab(view === "register")} onClick={() => setView("register")}>
            📋 Invoices <span style={{ fontWeight: 400, opacity: 0.8 }}>· all invoices, newest first</span>
          </span>
          <span style={segTab(view === "buckets")} onClick={() => setView("buckets")}>
            📅 Due buckets <span style={{ fontWeight: 400, opacity: 0.8 }}>· collections — what to chase</span>
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", fontSize: 12.5 }}>
          {isAdmin && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: "#94A3B8", textTransform: "uppercase", letterSpacing: ".05em" }}>Generate invoices</span>
              <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, color: "#475569", cursor: "pointer", margin: 0 }}>
                <input type="checkbox" checked={genDryRun} onChange={(e) => setGenDryRun(e.target.checked)} />
                Dry run (preview only)
              </label>
              <Button size="sm" variant="outline-secondary" disabled={!!genBusy} onClick={runReconcileCto}>
                {genBusy === "RECONCILE" ? <Spinner size="sm" animation="border" /> : "Reconcile CTO"}
              </Button>
              {!genDryRun && <span style={{ fontSize: 11, color: "#DC2626", fontWeight: 600 }}>⚠ live — creates invoices</span>}
            </div>
          )}
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={{ color: "#94A3B8", fontWeight: 600 }}>Terms:</span>
            <select value={planF} onChange={(e) => setPlanF(e.target.value)} style={SEL}>
              {Object.entries(PLANS).map(([k, p]) => <option key={k} value={k}>{p.label}</option>)}
            </select>
            <span style={{ color: "#94A3B8", fontWeight: 600, marginLeft: 8 }}>Rep:</span>
            <select value={repF} onChange={(e) => setRepF(e.target.value)} style={SEL}>
              <option value="">All reps</option>
              {repOptions.map((c) => <option key={c} value={c}>{repNames[c] || c}</option>)}
            </select>
            {view === "buckets" && bucket !== "reminders" && (
              <>
                <span style={{ color: "#94A3B8", fontWeight: 600, marginLeft: 8 }}>Payment:</span>
                <select value={payF} onChange={(e) => setPayF(e.target.value)} style={SEL}>
                  {Object.entries(PAYFILTERS).map(([k, p]) => <option key={k} value={k} title={p.sub}>{p.label}</option>)}
                </select>
                <span style={{ color: "#94A3B8", fontWeight: 600, marginLeft: 8 }}>Group by:</span>
                <select value={groupBy} onChange={(e) => setGroupBy(e.target.value)} style={SEL}>
                  {Object.entries(GROUPBYS).map(([k, g]) => <option key={k} value={k}>{g.label}</option>)}
                </select>
              </>
            )}
          </div>
        </div>
      </div>
      {genResult && (
        <Alert variant={genResult.ok ? (genResult.dry ? "info" : "success") : "danger"} dismissible onClose={() => setGenResult(null)}
          style={{ padding: "8px 12px", fontSize: 13, marginBottom: 12 }}>
          <strong>{genResult.msg}</strong>
          {genResult.preview?.length > 0 && (
            <div style={{ marginTop: 6, fontSize: 12, color: "#475569" }}>
              {genResult.preview.slice(0, 10).map((it, i) => (
                <div key={i}>#{it.orderNum} · {it.customerName || "—"} · pack {it.packSlipNum} · PO {it.poNum || "—"}</div>
              ))}
              {genResult.preview.length > 10 && <div>…and {genResult.preview.length - 10} more</div>}
            </div>
          )}
          {genResult.failures?.length > 0 && (
            <div style={{ marginTop: 6, fontSize: 12, color: "#B91C1C" }}>
              {genResult.failures.length} failure(s): {genResult.failures.slice(0, 3).join("; ")}
            </div>
          )}
        </Alert>
      )}

      {/* Bucket tabs (collections only) */}
      {view === "buckets" && (
        <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "nowrap" }}>
          {/* The four aging buckets share the row equally; Reminders keeps its natural width. */}
          {Object.entries(BUCKETS).map(([key, bk]) => (
            <span key={key}
              style={{ ...chip(bucket === key), flex: 1, textAlign: "center", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", borderColor: key === "overdue" && bucket === key ? "#DC2626" : undefined }}
              onClick={() => setBucket(key)}>
              {bk.label}
              {counts[key] && <span style={{ marginLeft: 6, fontWeight: 700 }}>{counts[key].count}</span>}
              {counts[key] && <span style={{ marginLeft: 6, fontWeight: 400, color: "#94A3B8" }}>{money(counts[key].remaining)}</span>}
            </span>
          ))}
          <span style={{ ...chip(bucket === "reminders"), flex: "0 0 auto", textAlign: "center", whiteSpace: "nowrap" }} onClick={() => setBucket("reminders")}>🔔 Reminders</span>
        </div>
      )}


      {/* Results scroll internally (sticky column headers) so the page stays put. */}
      <div className="dyn-results-wrap">
      <div className="dyn-results-scroll">
      {error && <Alert variant="danger" style={{ fontSize: 13 }}>{error}</Alert>}

      {loading ? (
        <div style={{ textAlign: "center", padding: 48, color: "#64748B" }}>
          <Spinner animation="border" size="sm" style={{ marginRight: 8 }} />
          {view === "register"
            ? "Loading invoices…"
            : `Loading ${b?.label || "reminders"}… large buckets read live balances from Epicor (this can take ~20s)`}
        </div>
      ) : view === "buckets" && bucket === "reminders" ? (
        <RemindersView />
      ) : view === "register" && data?.kind === "register" ? (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginBottom: 12 }}>
            <Kpi label="Invoices" value={data.total ?? registerRows.length} />
            <Kpi label="Total invoiced" value={money(data.totalAmount)} />
            <Kpi label="Awaiting PO" value={registerRows.filter((i) => (i.installmentsAwaitingPo || 0) > 0).length} tone="#B45309" />
            <Kpi label="Not sent" value={registerRows.filter((i) => i.sendStatus !== "Sent").length} tone="#DC2626" />
          </div>
          <div style={{ border: "1px solid #E2E8F0", borderRadius: 12, background: "#fff" }}>
            {registerRows.length === 0
              ? <div style={{ textAlign: "center", padding: 32, color: "#64748B" }}>No invoices match these filters.</div>
              : (
                <table className="dyn-table-dense fin-table" style={{ width: "100%", tableLayout: "fixed", borderCollapse: "collapse", fontSize: 13 }}>
                  <colgroup>
                    <col style={{ width: "9%" }} />
                    <col style={{ width: "17%" }} />
                    <col style={{ width: "7%" }} />
                    <col style={{ width: "12%" }} />
                    <col style={{ width: "9%" }} />
                    <col style={{ width: "10%" }} />
                    <col style={{ width: "10%" }} />
                    <col style={{ width: "9%" }} />
                    <col style={{ width: "9%" }} />
                    <col style={{ width: "8%" }} />
                  </colgroup>
                  <thead>
                    <tr style={HEAD_TR}>
                      <th style={TH}>Invoice #</th>
                      <th style={TH}>Customer</th>
                      <th style={TH}>Plan</th>
                      <th style={TH}>Sales Rep</th>
                      <th style={{ ...TH, textAlign: "right" }}>Amount</th>
                      <th style={TH}>Invoice Date</th>
                      <th style={TH}>Match</th>
                      <th style={TH}>Sent</th>
                      <th style={TH}>PO</th>
                      <th style={TH}>Held</th>
                    </tr>
                    <tr>
                      {["invoice", "customer", "plan", "rep", "amount", "date", "match", "sent", "po", "held"].map((k) => (
                        <th key={k} style={THF}><Form.Control size="sm" placeholder="Filter…" value={colF[k] || ""} onChange={(e) => setCol(k, e.target.value)} /></th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {visibleRegister.length === 0
                      ? <tr><td colSpan={10} style={{ textAlign: "center", padding: 24, color: "#94A3B8" }}>No rows match the column filters.</td></tr>
                      : visibleRegister.map((i) => <RegisterTr key={i.invoiceNum} i={i} />)}
                  </tbody>
                </table>
              )}
          </div>
        </>
      ) : data?.kind === "bucket" ? (
        <>
          <div style={{ fontSize: 12, color: "#94A3B8", marginBottom: 8 }}>
            {b?.label} · fromDays={b?.fromDays} · toDays={b?.toDays}
          </div>
          {notLive && (
            <Alert variant="warning" style={{ fontSize: 13 }}>
              ⚠ Live Epicor balances unavailable — showing scheduled amounts, so already-paid
              installments may still appear and amounts are not net of payments right now.
            </Alert>
          )}
          {payF === "paid" && notLive ? (
            <div style={{ textAlign: "center", padding: 32, color: "#64748B" }}>
              Payment status is netted from a live Epicor balance read, which is unavailable right now —
              so nothing can be shown as paid.
            </div>
          ) : (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginBottom: 12 }}>
                <Kpi label={payF === "paid" ? "Invoices covered" : "Invoices to chase"} value={bucketRows.length} />
                <Kpi label="Payments due" value={bucketRows.reduce((s, g) => s + g.seqs.length, 0)} />
                <Kpi label={payF === "paid" ? "Already received" : "Total still owed"}
                  value={money(bucketRows.reduce((s, g) => s + (payF === "paid" ? (g.paidIn || g.gross - g.rem) : g.rem), 0))}
                  tone={payF === "paid" ? "#16A34A" : undefined} />
                <Kpi label="Overdue" value={bucketRows.filter((g) => g.minDays < 0).length} tone="#DC2626" />
              </div>
              <div style={{ border: "1px solid #E2E8F0", borderRadius: 12, background: "#fff" }}>
                {bucketRows.length === 0 ? (
                  <div style={{ textAlign: "center", padding: 32, color: "#64748B" }}>Nothing in this window.</div>
                ) : (
                  <table className="dyn-table-dense fin-table" style={{ width: "100%", tableLayout: "fixed", borderCollapse: "collapse", fontSize: 13 }}>
                    <colgroup>
                      <col style={{ width: "8%" }} />
                      <col style={{ width: "14%" }} />
                      <col style={{ width: "6%" }} />
                      <col style={{ width: "10%" }} />
                      <col style={{ width: "10%" }} />
                      <col style={{ width: "7%" }} />
                      <col style={{ width: "7%" }} />
                      <col style={{ width: "7%" }} />
                      <col style={{ width: "9%" }} />
                      <col style={{ width: "8%" }} />
                      <col style={{ width: "8%" }} />
                      <col style={{ width: "6%" }} />
                    </colgroup>
                    <thead>
                      <tr style={HEAD_TR}>
                        <th style={TH}>Invoice #</th>
                        <th style={TH}>Customer</th>
                        <th style={TH}>Plan</th>
                        <th style={TH}>Sales Rep</th>
                        <th style={TH}>Installments</th>
                        <th style={TH}>Sent</th>
                        <th style={TH}>PO</th>
                        <th style={TH}>Match</th>
                        <th style={{ ...TH, textAlign: "right" }}>Amount Due</th>
                        <th style={{ ...TH, textAlign: "right" }}>Paid So Far</th>
                        <th style={TH}>Due Date(s)</th>
                        <th style={TH}>Due</th>
                      </tr>
                      <tr>
                        {["invoice", "customer", "plan", "rep", "inst", "sent", "po", "match", "amount", "paid", "due", "days"].map((k) => (
                          <th key={k} style={THF}><Form.Control size="sm" placeholder="Filter…" value={colF[k] || ""} onChange={(e) => setCol(k, e.target.value)} /></th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {visibleBucket.length === 0 ? (
                        <tr><td colSpan={12} style={{ textAlign: "center", padding: 24, color: "#94A3B8" }}>No rows match the column filters.</td></tr>
                      ) : grouped ? (
                        grouped.map((grp) => (
                          <React.Fragment key={grp.key}>
                            <tr style={{ cursor: "pointer" }} onClick={() => setOpenGroups((o) => ({ ...o, [grp.key]: !o[grp.key] }))}>
                              <td colSpan={12} data-label="Group" style={{ background: "#F8FAFC" }}>
                                <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                                  <div style={{ flex: 1 }}>
                                    <div style={{ fontWeight: 700, fontSize: 14 }}>
                                      {groupBy === "hospital" ? "🏥 " : groupBy === "rep" ? "👤 " : "🗺 "}{grp.key}
                                    </div>
                                    <div style={{ fontSize: 12, color: "#64748B" }}>{grp.invoices.length} invoice(s)</div>
                                    <div style={{ display: "flex", gap: 8, fontSize: 12, marginTop: 2 }}>
                                      {grp.over90 > 0.004 && <span style={{ color: "#DC2626", fontWeight: 600 }}>{money(grp.over90)} over 90d</span>}
                                      {grp.unsent > 0 && <span style={{ color: "#B45309" }}>{grp.unsent} un-sent</span>}
                                      {grp.noPo > 0 && <span style={{ color: "#94A3B8" }}>{grp.noPo} no PO</span>}
                                    </div>
                                  </div>
                                  <div style={{ textAlign: "right" }}>
                                    <div style={{ fontWeight: 800, fontSize: 15, color: payF === "paid" ? "#16A34A" : undefined }}>{money(grp.total)}</div>
                                    {grp.minDays < 0 && payF !== "paid" && <div style={{ fontSize: 12, color: "#DC2626" }}>oldest {-grp.minDays}d late</div>}
                                    <div style={{ fontSize: 14, color: "#94A3B8" }}>{openGroups[grp.key] ? "▾" : "▸"}</div>
                                  </div>
                                </div>
                              </td>
                            </tr>
                            {openGroups[grp.key] && grp.invoices.map((g) => <BucketTr key={g.parentInvoiceNum} g={g} />)}
                          </React.Fragment>
                        ))
                      ) : (
                        visibleBucket.map((g) => <BucketTr key={g.parentInvoiceNum} g={g} />)
                      )}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          )}
        </>
      ) : null}
      </div>
      </div>
    </div>
    </div>
  );
}
